import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Provider, useSelector, useDispatch } from "react-redux";
import { verifyToken } from "./redux/AuthSlice";

import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner/LoadingSpinner";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Sidebar from "./components/Sidebar/Sidebar";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { store } from "./redux/store"
import Certificates from "./pages/Certificates/Certificates";

import ToastContainer from './components/Toast/Toast';
import useToast from './components/Toast/useToast'
import { ToastProvider, useGlobalToast } from './components/Toast/ToastContext';


// Lazy-loaded pages
const HomePage = lazy(() => import("./pages/Home/HomePage"));
const LoginPage = lazy(() => import("./pages/Login/LoginPage"));
const RegisterPage = lazy(() => import("./pages/Register/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AdminDashboard = lazy(() => import("./pages/AdminPanelPage"));
const CoursesPage = lazy(() => import("./pages/Course/CoursesPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseView/DynamicCourseView"));
const PracticePage = lazy(() => import("./pages/PracticePage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const InternshipsPage = lazy(() => import("./pages/Internship/InternshipsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutUs/AboutPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const JobProfile = lazy(() => import("./pages/JobPortalPage"));
const ResumePage = lazy(() => import("./pages/MyResumePage"));
const MyLearningPage = lazy(() => import("./pages/MyLearningPage"));
const ManagementPage = lazy(() => import("./pages/ManagementPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));


// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { user, loading } = useSelector(state => state.auth);

  if (loading) return <LoadingSpinner />;

  return (
    <ErrorBoundary>
      <Navbar />
      <div className="main-container-with-sidebar" style={{ paddingTop: '60px' }}>
        <aside>
          <Sidebar />
        </aside>
        <main style={{ maxHeight: "calc(100vh - 60px)", overflowY: "auto", width: "100%", padding: "1rem" }}>
          <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
        </main>
      </div>
    </ErrorBoundary>
  );
};

// Public Layout
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hiddenFooterRoutes = ["/login", "/register", "/forgot-password"];
  const shouldHideFooter = hiddenFooterRoutes.includes(location.pathname);

  return (
    <ErrorBoundary>
      <Navbar />
      <main style={{ minHeight: "calc(100vh - 60px)", paddingTop: "60px", width: "100%" }}>
        <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
      </main>
      {!shouldHideFooter && <Footer />}
    </ErrorBoundary>
  );
};

// Role-based dashboard selector
const DashboardSelector = () => {
  const { user } = useSelector(state => state.auth);
  switch (user?.role?.toLowerCase()) {
    case 'admin':
      return <AdminDashboard />;
    case 'user':
    case 'student':
    default:
      return <DashboardPage />;
  }
};

// App routes
const AppRoutes = () => {
  const { isAuthenticated, loading } = useSelector(state => state.auth);

  if (loading) return <LoadingSpinner />;

  return (
    <Routes>
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <PublicLayout><LoginPage /></PublicLayout>} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <PublicLayout><RegisterPage /></PublicLayout>} />
      <Route path="/forgot-password" element={<PublicLayout><ForgotPasswordPage /></PublicLayout>} />
      <Route path="/courses" element={<PublicLayout><CoursesPage /></PublicLayout>} />
      <Route path="/certificates" element={<PublicLayout><Certificates /></PublicLayout>} />

      <Route path="/courses/:id" element={<PublicLayout><CourseDetailPage /></PublicLayout>} />
      <Route path="/internships" element={<PublicLayout><InternshipsPage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardSelector /></DashboardLayout></ProtectedRoute>} />
      <Route path="/my-learning" element={<ProtectedRoute allowedRoles={['user', 'student', 'admin']}><DashboardLayout><MyLearningPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute allowedRoles={['user', 'student', 'admin']}><DashboardLayout><JobProfile /></DashboardLayout></ProtectedRoute>} />
      <Route path="/my-resume" element={<ProtectedRoute allowedRoles={['user', 'student', 'admin']}><DashboardLayout><ResumePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/practice" element={<ProtectedRoute allowedRoles={['user', 'student', 'admin']}><DashboardLayout><PracticePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute allowedRoles={['user', 'student', 'admin']}><DashboardLayout><LeaderboardPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/management" element={<ProtectedRoute allowedRoles={['admin', 'instructor']}><DashboardLayout><ManagementPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />

    </Routes>
  );
};

// Main App
function AppWrapper() {
  const dispatch = useDispatch();
  const { toasts, removeToast } = useGlobalToast();


  useEffect(() => {
    dispatch(verifyToken());
  }, [dispatch]);

  return <>

    <AppRoutes />;
    <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

  </>
}

function App() {

  const { toasts, removeToast } = useToast();

  return (
    <Provider store={store}>
      <HelmetProvider>
        <ErrorBoundary>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <ToastProvider>
              <Router>
                <AppWrapper />
              </Router>̥
            </ToastProvider>


          </GoogleOAuthProvider>
        </ErrorBoundary>
      </HelmetProvider>
    </Provider>
  );
}

export default App;